import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { authService } from '../../services/api';
import { organisationService } from '../../services/api/organisations';
import { Organisation } from '../../types/api';
import { Button } from '../ui/Button';
import { ThemeToggle } from '../ThemeToggle';
import { PWAInstallPrompt, ConnectionStatus } from '../PWAInstallPrompt';
import { apiClient } from '../../services/api/client';
import { NotificationBell } from '../NotificationBell';
import PushNotificationPrompt from '../PushNotificationPrompt';
import { usePushNotifications } from '../../hooks/usePushNotifications';

interface MainLayoutProps {
  children: React.ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
  const { user, isAuthenticated, isLoading } = useCurrentUser();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userOrganisation, setUserOrganisation] = useState<Organisation | null>(null);
  const [showPushPrompt, setShowPushPrompt] = useState(false);

  // Initialiser le système de push notifications
  const {
    isSupported,
    isSubscribed,
    isLoading: pushLoading,
    vapidPublicKey,
    subscribe,
    unsubscribe
  } = usePushNotifications();

  useEffect(() => {
    const loadUserOrganisation = async () => {
      if (user?.organisationId) {
        try {
          const organisations = await organisationService.getOrganisations();
          const org = organisations.find(o => o.id === user.organisationId);
          console.log('Organisation trouvée:', org);
          setUserOrganisation(org || null);
        } catch (error) {
          console.error('Erreur lors du chargement de l\'organisation:', error);
        }
      }
    };

    if (isAuthenticated) {
      loadUserOrganisation();
    }
  }, [user?.organisationId, isAuthenticated]);

  // Forcer le rafraîchissement des données utilisateur au montage
  useEffect(() => {
    if (isAuthenticated) {
      console.log('🔄 [MAIN LAYOUT] Forcer le rafraîchissement des données utilisateur');
    }
  }, [isAuthenticated]);

  // Abonnement automatique aux push notifications après connexion
  useEffect(() => {
    if (isAuthenticated && isSupported && vapidPublicKey && !isSubscribed) {
      console.log('🔔 [MAIN LAYOUT] Tentative d\'abonnement automatique aux push notifications');
      
      // Vérifier si l'utilisateur a déjà refusé récemment (dans les dernières 24h)
      const lastDecline = localStorage.getItem('pushNotificationsLastDecline');
      const hasRecentlyDeclined = lastDecline && (Date.now() - parseInt(lastDecline)) < (24 * 60 * 60 * 1000);
      
      if (!hasRecentlyDeclined) {
        console.log('🔔 [MAIN LAYOUT] Abonnement automatique déclenché');
        
        // Essayer de s'abonner automatiquement après un court délai
        const timer = setTimeout(async () => {
          try {
            console.log('🔔 [MAIN LAYOUT] Début de l\'abonnement automatique');
            const success = await subscribe();
            if (success) {
              console.log('🔔 [MAIN LAYOUT] Abonnement automatique réussi');
            } else {
              console.log('🔔 [MAIN LAYOUT] Abonnement automatique échoué, affichage du prompt');
              // Si l'abonnement automatique échoue (permissions refusées), afficher le prompt
              const hasPermanentlyDeclined = localStorage.getItem('pushNotificationsPermanentlyDeclined');
              if (!hasPermanentlyDeclined) {
                setShowPushPrompt(true);
              }
            }
          } catch (error) {
            console.log('🔔 [MAIN LAYOUT] Erreur lors de l\'abonnement automatique:', error);
            // En cas d'erreur inattendue, afficher aussi le prompt
            const hasPermanentlyDeclined = localStorage.getItem('pushNotificationsPermanentlyDeclined');
            if (!hasPermanentlyDeclined) {
              setShowPushPrompt(true);
            }
          }
        }, 3000); // 3 secondes après la connexion pour laisser le temps au service worker de s'activer

        return () => clearTimeout(timer);
      } else {
        console.log('🔔 [MAIN LAYOUT] Abonnement automatique ignoré - utilisateur a refusé récemment');
      }
    }
  }, [isAuthenticated, isSupported, isSubscribed, vapidPublicKey, subscribe]);

  const getLogoUrl = (logoFilename: string | null | undefined) => {
    if (!logoFilename) return null;
    // Utiliser la même approche que pour les pièces jointes
    const url = `http://localhost:5555/api/files/logos/${logoFilename}`;
    console.log('URL du logo:', url);
    return url;
  };

  const handleLogoError = async (e: React.SyntheticEvent<HTMLImageElement, Event>, logoFilename: string) => {
    console.error('Erreur de chargement du logo via URL directe, tentative avec blob:', e);

    try {
      // Récupérer le logo via une requête authentifiée avec apiClient
      const response = await apiClient.get(`/files/logos/${logoFilename}`, {
        responseType: 'blob'
      });

      // Créer une URL blob pour l'image
      const blobUrl = URL.createObjectURL(response);

      // Remplacer la source de l'image par l'URL blob
      const imgElement = e.currentTarget;
      if (imgElement) {
        imgElement.src = blobUrl;
        console.log('Logo chargé avec succès via blob');
      }
    } catch (error) {
      console.error('Erreur lors du chargement du logo via blob:', error);
      // En cas d'erreur, cacher l'image
      const imgElement = e.target as HTMLImageElement;
      if (imgElement) {
        imgElement.style.display = 'none';
      }
    }
  };

  const navigation = useMemo(() => {
    const items = [
      { name: 'Tableau de bord', to: '/dashboard', icon: '📊' },
      { name: 'Courriers', to: '/courriers', icon: '📝' },
      { name: 'Organisations', to: '/organisations', icon: '🏢' },
    ];
    const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
    const isSuperAdmin = user?.role === 'SUPER_ADMIN';
    if (isAdmin) {
      items.push({ name: 'Utilisateurs', to: '/users', icon: '👤' });
    }
    if (isSuperAdmin) {
      items.push({ name: 'Périmètres', to: '/perimeters', icon: '🌍' });
    }
    return items;
  }, [user?.role]);

  const handleLogout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (_) {
      // ignore network/logout error, still force local clear by service
    } finally {
      window.location.href = '/login';
    }
  }, []);

  const handlePushPromptAccept = () => {
    console.log('✅ Utilisateur a accepté les push notifications');
    localStorage.removeItem('pushNotificationsLastDecline');
    localStorage.removeItem('pushNotificationsPermanentlyDeclined');
  };

  const handlePushPromptDecline = () => {
    console.log('❌ Utilisateur a refusé les push notifications');
    localStorage.setItem('pushNotificationsLastDecline', Date.now().toString());
  };

  const handlePushPromptClose = () => {
    setShowPushPrompt(false);
  };

  if (!isAuthenticated) {
    return <div className="min-h-screen bg-gray-50">{children}</div>;
  }

  const navLinkClass = (isActive: boolean) =>
    `group flex gap-x-3 rounded-lg p-3 text-base font-medium transition-colors ${isActive
      ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
    }`;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="relative flex w-full max-w-xs flex-col bg-white dark:bg-gray-800 shadow-xl">
          <div className="absolute top-0 right-0 -mr-12 pt-4">
            <button
              type="button"
              className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <span className="sr-only">Fermer le menu</span>
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex h-16 shrink-0 items-center justify-between px-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <img
                src="/images/logo-digimanager.png"
                alt="DigiManager Logo"
                className="w-auto object-contain"
              />
            </div>
            <ThemeToggle />
          </div>

          <nav className="flex flex-1 flex-col gap-y-7 px-6 py-6">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  {navigation.map((item) => (
                    <li key={item.name}>
                      <NavLink
                        to={item.to}
                        className={({ isActive }) => navLinkClass(isActive)}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <span className="text-lg">{item.icon}</span>
                        {item.name}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>
            </ul>

            {/* User info and logout */}
            <div className="mt-auto border-t border-gray-200 dark:border-gray-700 pt-6">
              <div className="flex items-center gap-3 px-3">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center">
                    <span className="text-white font-medium text-sm">
                      {user?.fullName?.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {user?.fullName}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                    {user?.email}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </Button>
              </div>
            </div>
          </nav>
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 px-6 pb-4">
          <div className="flex h-16 shrink-0 items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="/images/logo-digimanager.png"
                alt="DigiManager Logo"
                className="w-auto object-contain"
              />
            </div>
            <ThemeToggle />
          </div>

          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  {navigation.map((item) => (
                    <li key={item.name}>
                      <NavLink
                        to={item.to}
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span className="text-lg">{item.icon}</span>
                        {item.name}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>
            </ul>

            {/* User info and logout */}
            <div className="mt-auto border-t border-gray-200 dark:border-gray-700 pt-6">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center">
                    <span className="text-white font-medium text-sm">
                      {user?.fullName?.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {user?.fullName}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                    {user?.email}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </Button>
              </div>
            </div>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 dark:text-gray-300 lg:hidden hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Ouvrir le menu</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1"></div>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {/* Cloche de notifications */}
              <NotificationBell />

              {/* Logo du périmètre pour utilisateurs non superadmin, logo de l'organisation pour superadmin */}
              {(user?.role !== 'SUPER_ADMIN' ? user?.perimeter?.logo : userOrganisation?.logo) && (
                <div className="flex items-center gap-2 mt-9">
                  <img
                    src=""
                    alt={`Logo ${user?.role !== 'SUPER_ADMIN' ? user?.perimeter?.displayName : userOrganisation?.displayName}`}
                    className="w-24 object-contain rounded"
                    style={{ width: '80px', height: 'auto' }}
                    onLoad={() => {
                      console.log('Logo chargé avec succès');
                    }}
                    ref={async (imgElement) => {
                      if (imgElement) {
                        try {
                          const logoFilename = user?.role !== 'SUPER_ADMIN' ? user?.perimeter?.logo : userOrganisation?.logo;
                          if (!logoFilename) return;

                          // Extraire le sous-dossier et le nom de fichier du chemin complet
                          const pathParts = logoFilename.split('/');
                          const subfolder = pathParts[pathParts.length - 2]; // "perimeter-logos"
                          const filenameOnly = pathParts[pathParts.length - 1]; // "moudoun-beautiful-rmbg_1760730109990.png"

                          // Récupérer le logo via une requête authentifiée avec apiClient
                          const response = await apiClient.get(`/files/${subfolder}/${filenameOnly}`, {
                            responseType: 'blob'
                          });

                          // Créer une URL blob pour l'image
                          const blobUrl = URL.createObjectURL(response);

                          // Définir la source de l'image
                          imgElement.src = blobUrl;

                          console.log('Logo chargé avec succès via blob');
                        } catch (error) {
                          console.error('Erreur lors du chargement du logo via blob:', error);
                          // En cas d'erreur, cacher l'image
                          imgElement.style.display = 'none';
                        }
                      }
                    }}
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-300 hidden sm:block">
                    {user?.role !== 'SUPER_ADMIN' ? user?.perimeter?.displayName : userOrganisation?.displayName}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-8">
          <div className="px-4 sm:px-6 lg:px-8">
            {/* PWA Install Prompt */}
            <div className="mb-6">
              <PWAInstallPrompt variant="banner" />
            </div>

            {/* Connection Status */}
            <div className="mb-6">
              <ConnectionStatus />
            </div>

            {children}
          </div>
        </main>
      </div>

      {/* Push Notification Prompt */}
      <PushNotificationPrompt
        isOpen={showPushPrompt}
        onClose={handlePushPromptClose}
        onAccept={handlePushPromptAccept}
        onDecline={handlePushPromptDecline}
      />
    </div>
  );
}